<template>
    <div class="grid">
        <div class="unit whole">
            <h1>Allocate pupils</h1>
            <p class="lead">Allocate pupils to classes.</p>
        </div>
    </div>
    <div v-if="pupils.length > 0" class="grid">
        <div class="unit whole">
            <div class="panel">
                <v-card :disabled="selectedRows.length == 0" title="Multiple add or remove" class="mb-3">
                    <v-card-subtitle>
                        <span v-if="selectedRows.length == 0">
                            Select pupils to add and remove them from classes.
                        </span>
                        <span v-else>
                            Select the classes to add and remove the {{ selectedRows.length }} selected pupil{{
                                selectedRows.length > 1 ? 's' : ''
                            }}
                            to/from.</span
                        >
                    </v-card-subtitle>
                    <v-card-text>
                        <div class="d-flex">
                            <v-select
                                v-model="addToClasses"
                                label="Add to classes"
                                :items="classes"
                                item-title="name"
                                item-value="uid"
                                density="compact"
                                hide-details
                                multiple
                                chips
                                class="mr-2"
                            >
                                <template #chip="{ item: c }">
                                    <v-chip
                                        :text="c.title"
                                        :color="colorNames[hash(c.value) % colorNames.length]"
                                    ></v-chip>
                                </template>
                            </v-select>

                            <v-select
                                v-model="removeFromClasses"
                                label="Remove from classes"
                                :items="classes"
                                item-title="name"
                                item-value="uid"
                                density="compact"
                                hide-details
                                multiple
                                chips
                            >
                                <template #chip="{ item: c }">
                                    <v-chip
                                        :text="c.title"
                                        :color="colorNames[hash(c.value) % colorNames.length]"
                                    ></v-chip>
                                </template>
                            </v-select>
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <a class="btn ml-2" :class="{ disabled: selectedRows.length == 0 }" @click="updateClassesBulk"
                            >Save</a
                        >
                    </v-card-actions>
                </v-card>
                <div class="d-flex justify-space-between">
                    <div class="w-25">
                        <v-text-field
                            v-model="search"
                            label="Search"
                            clearable
                            hide-details
                            density="compact"
                        ></v-text-field>
                    </div>
                    <div class="w-25">
                        <v-select
                            v-model="filterClass"
                            label="Filter by class"
                            :items="
                                classes.concat(
                                    createReactive({ name: 'unallocated', uid: 'unallocated' } as unknown as Class),
                                )
                            "
                            item-title="name"
                            item-value="uid"
                            clearable
                            density="compact"
                            hide-details
                        >
                        </v-select>
                    </div>
                </div>
                <v-data-table
                    v-model="selectedRows"
                    v-model:sort-by="sortBy"
                    show-select
                    density="compact"
                    items-per-page="-1"
                    :headers="headers"
                    :items="items"
                    item-value="uid"
                    class="elevation-0"
                >
                    <template #bottom></template>
                    <template #[`item.classes`]="{ item }">
                        <v-select
                            v-model="item.classes"
                            class="my-2"
                            :items="classItems"
                            item-title="name"
                            item-value="uid"
                            chips
                            color="blue-grey-lighten-2"
                            label="Select classes"
                            multiple
                            hide-details
                            return-object
                            @update:model-value="updateClasses(item, item.classes)"
                        >
                            <template #chip="{ item: c }">
                                <v-chip :text="c.title" :color="colorNames[hash(c.value) % colorNames.length]"></v-chip>
                            </template>
                        </v-select>
                    </template>
                </v-data-table>
            </div>
        </div>
    </div>
    <v-overlay
        :model-value="!isLoaded.pupils || !isLoaded.classes"
        class="align-center justify-center"
        scroll-strategy="none"
    >
        <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
    </v-overlay>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { VDataTable } from 'vuetify/components/VDataTable';
import { Class, PupilOverview } from '@ilteducation/giglets-types';

definePage({ meta: { area: 'pupils' } });
const route = useRoute();

const confirm = useConfirmDialog();
const { snackbarSuccess } = useSnackbar();
const store = useAdminStore();
store.fetchPupils();
store.fetchClasses();
const { pupils, classes, isLoaded } = storeToRefs(store);

const search = ref<string>('');
const filterClass = ref<string>();
if (route.query.class) {
    filterClass.value = route.query.class.toString();
}
const addToClasses = ref<string[]>([]);
const removeFromClasses = ref<string[]>([]);
const sortBy = ref<VDataTable['sortBy']>([{ key: 'name', order: 'asc' }]);
const selectedRows = ref<Array<string>>([]);

watch(selectedRows, (newValue, oldValue) => {
    if (oldValue.length === 1 && newValue.length === 2) {
        // clear before showing the card
        addToClasses.value = [];
        removeFromClasses.value = [];
    }
});

const classItems = computed(() => {
    return classes.value.map((c) => ({ uid: c.uid, name: c.name }));
});

const items = computed(() =>
    pupils.value
        .filter(
            (p) =>
                typeof filterClass.value !== 'string' ||
                (filterClass.value === 'unallocated' && !p.classes.length) ||
                p.classes.map((c) => c.uid).includes(filterClass.value),
        )
        .filter((p) => (p.name + p.username).toLowerCase().includes(search.value.toLowerCase())),
);

async function updateClasses(pupil: PupilOverview, newClasses: { uid: string; name: string }[]) {
    const newClassUids = newClasses.map((c) => c.uid);
    // To find the class the pupil has been added to, search through the school's classes:
    // * look only at the classes in newClasses
    // * find the class where the pupil isn't a member
    //
    // To find the class the pupil has been removed from, search through the school's classes:
    // * look only at the classes NOT in newClasses
    // * find the class where the pupil is a member
    const addedToClass = classes.value
        .filter((c) => newClassUids.includes(c.uid))
        .find((c) => !c.pupils.map((p) => p.id).includes(pupil.id));
    const removedFromClass = classes.value
        .filter((c) => !newClassUids.includes(c.uid))
        .find((c) => c.pupils.map((p) => p.id).includes(pupil.id));

    if (addedToClass) {
        await store.updateClass(addedToClass.uid, { addPupils: [pupil.uid] });
    }
    if (removedFromClass) {
        await store.updateClass(removedFromClass.uid, { removePupils: [pupil.uid] });
    }
    snackbarSuccess('Pupil classes have been updated.');
}

async function updateClassesBulk() {
    const res = await confirm(
        'Update classes',
        `Are you sure you want to update the classes of the ${selectedRows.value.length} selected pupils?`,
    );
    if (res) {
        await Promise.all(
            [
                addToClasses.value.map((classUid) => {
                    return store.updateClass(classUid, { addPupils: selectedRows.value });
                }),
                removeFromClasses.value.map((classUid) => {
                    return store.updateClass(classUid, { removePupils: selectedRows.value });
                }),
            ].flat(),
        );
        snackbarSuccess('Pupil classes have been updated.');
        await store.fetchPupils();
        await store.fetchClasses();
        addToClasses.value = [];
        removeFromClasses.value = [];
        selectedRows.value = [];
    }
}

const colorNames = ['red', 'pink', 'purple', 'indigo', 'blue', 'cyan', 'teal', 'green', 'orange', 'brown'];

const headers = [
    {
        title: 'Name',
        key: 'name',
        sortable: true,
    },
    {
        title: 'Username',
        key: 'username',
        sortable: true,
    },
    {
        title: 'Classes',
        key: 'classes',
        sortable: false,
    },
];

function createReactive(o: any) {
    return reactive(o);
}

const hash = (str: string) => {
    let hash = 0;
    for (const char of str) {
        hash = (hash << 5) - hash + char.charCodeAt(0);
        hash |= 0; // Constrain to 32bit integer
    }
    return hash;
};
</script>
<style scoped lang="scss">
:deep(th) {
    vertical-align: revert;
}
:deep(td) {
    text-align: revert;
    vertical-align: revert;
    border-top: revert;
}
</style>
